import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Loader2, LogOut, Mail, Trash2, Plus, Inbox, MessageSquare,
  HelpCircle, MapPinned, Calendar, BarChart3, ExternalLink,
} from "lucide-react";
import { LogoEmblem } from "../components/Logo";

const TABS = [
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "blog", label: "Insights", icon: MessageSquare },
  { id: "testimonials", label: "Testimonials", icon: MessageSquare },
  { id: "faqs", label: "FAQs", icon: HelpCircle },
  { id: "programs", label: "Programs", icon: MapPinned },
  { id: "events", label: "Events", icon: Calendar },
];

const STATUS_OPTIONS = ["new", "contacted", "qualified", "closed"];

function StatusBadge({ status }) {
  const colors = {
    new: "bg-gold-500 text-navy",
    contacted: "bg-blue-500/15 text-blue-700 border border-blue-500/30",
    qualified: "bg-emerald-500/15 text-emerald-700 border border-emerald-500/30",
    closed: "bg-slate-300/40 text-slate-700 border border-slate-300",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold ${
        colors[status] || colors.new
      }`}
    >
      {status}
    </span>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="bg-white border border-slate-200 p-6">
      <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
        {label}
      </div>
      <div className="mt-3 font-display text-3xl text-navy">{value}</div>
      {hint ? <div className="mt-2 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [blog, setBlog] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);

  const reloadAll = async () => {
    try {
      const [s, l, b, t, f, p, e] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/blog"),
        api.get("/testimonials"),
        api.get("/faqs"),
        api.get("/programs"),
        api.get("/events"),
      ]);
      setStats(s.data);
      setLeads(l.data);
      setBlog(b.data);
      setTestimonials(t.data);
      setFaqs(f.data);
      setPrograms(p.data);
      setEvents(e.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    if (user) reloadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  // ---------- Lead status update ----------
  const updateLeadStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((arr) => arr.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Lead marked as ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ---------- Generic delete ----------
  const del = async (kind, id) => {
    if (!window.confirm("Delete this item?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Deleted");
      reloadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  // ---------- Generic create ----------
  const createItem = async (kind, body) => {
    setBusy(true);
    try {
      await api.post(`/admin/${kind}`, body);
      toast.success("Created");
      reloadAll();
      return true;
    } catch (err) {
      toast.error(formatApiError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F4F6FB]">
      <header className="bg-navy text-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoEmblem size={44} variant="primary" />
            <div>
              <div className="font-display text-lg">Destineze Admin</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/60">
                {user.email}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/70 hover:text-gold"
            >
              View Site <ExternalLink size={12} />
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.18em] border border-white/20 hover:border-gold hover:text-gold transition"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
        {stats ? (
          <div
            data-testid="admin-stats"
            className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 mb-10"
          >
            <StatCard label="New Leads" value={stats.leads_new} hint={`of ${stats.leads} total`} />
            <StatCard label="Total Leads" value={stats.leads} />
            <StatCard label="Insights" value={stats.blog_posts} />
            <StatCard label="Testimonials" value={stats.testimonials} />
            <StatCard label="FAQs" value={stats.faqs} />
            <StatCard label="Programs" value={stats.programs} />
            <StatCard label="Events" value={stats.events} />
          </div>
        ) : null}

        <div
          data-testid="admin-tabs"
          className="flex flex-wrap gap-2 border-b border-slate-200 mb-8"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold transition border-b-2 -mb-px ${
                tab === t.id
                  ? "border-navy text-navy"
                  : "border-transparent text-slate-500 hover:text-navy"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" ? (
          <LeadsPanel leads={leads} onStatus={updateLeadStatus} />
        ) : null}
        {tab === "blog" ? (
          <BlogPanel
            blog={blog}
            onCreate={(b) => createItem("blog", b)}
            onDelete={(id) => del("blog", id)}
            busy={busy}
          />
        ) : null}
        {tab === "testimonials" ? (
          <TestimonialPanel
            items={testimonials}
            onCreate={(b) => createItem("testimonials", b)}
            onDelete={(id) => del("testimonials", id)}
            busy={busy}
          />
        ) : null}
        {tab === "faqs" ? (
          <FAQPanel
            items={faqs}
            onCreate={(b) => createItem("faqs", b)}
            onDelete={(id) => del("faqs", id)}
            busy={busy}
          />
        ) : null}
        {tab === "programs" ? (
          <ProgramPanel
            items={programs}
            onCreate={(b) => createItem("programs", b)}
            onDelete={(id) => del("programs", id)}
            busy={busy}
          />
        ) : null}
        {tab === "events" ? (
          <EventPanel
            items={events}
            onCreate={(b) => createItem("events", b)}
            onDelete={(id) => del("events", id)}
            busy={busy}
          />
        ) : null}
      </main>
    </div>
  );
}

// ---------- Panels ----------
function LeadsPanel({ leads, onStatus }) {
  return (
    <div data-testid="admin-leads-panel" className="bg-white border border-slate-200">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Inbox className="text-navy" size={16} />
        <div className="font-display text-lg text-navy">Inbound inquiries</div>
        <div className="ml-auto text-xs text-slate-500">{leads.length} total</div>
      </div>
      {leads.length === 0 ? (
        <div className="p-10 text-slate-500">No leads yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Type</th>
                <th className="px-6 py-3">Org</th>
                <th className="px-6 py-3">Email</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Received</th>
                <th className="px-6 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 align-top hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="font-semibold text-navy">{l.name}</div>
                    {l.role ? (
                      <div className="text-xs text-slate-500">{l.role}</div>
                    ) : null}
                    {l.country ? (
                      <div className="text-xs text-slate-400">{l.country}</div>
                    ) : null}
                    {l.message ? (
                      <div className="mt-2 text-xs text-slate-600 max-w-md">
                        {l.message}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4 text-slate-700 capitalize">
                    {l.inquiry_type}
                  </td>
                  <td className="px-6 py-4 text-slate-700">
                    {l.organization || "—"}
                  </td>
                  <td className="px-6 py-4">
                    <a
                      href={`mailto:${l.email}`}
                      className="inline-flex items-center gap-2 text-navy hover:text-gold-500"
                    >
                      <Mail size={12} /> {l.email}
                    </a>
                    {l.phone ? (
                      <div className="text-xs text-slate-500 mt-1">{l.phone}</div>
                    ) : null}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={l.status} />
                  </td>
                  <td className="px-6 py-4 text-slate-500 text-xs">
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <select
                      data-testid={`lead-status-${l.id}`}
                      value={l.status}
                      onChange={(e) => onStatus(l.id, e.target.value)}
                      className="border border-slate-300 px-2 py-1 text-xs"
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PanelShell({ title, children, formNode, testId }) {
  return (
    <div data-testid={testId} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7 bg-white border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="font-display text-lg text-navy">{title}</div>
        </div>
        {children}
      </div>
      <div className="lg:col-span-5">{formNode}</div>
    </div>
  );
}

function CreateBox({ title, onSubmit, fields, testId, busy }) {
  const [form, setForm] = useState(
    Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""]))
  );
  const change = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    for (const f of fields) {
      if (f.transform) payload[f.name] = f.transform(payload[f.name]);
    }
    const ok = await onSubmit(payload);
    if (ok) {
      setForm(Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""])));
    }
  };

  return (
    <form
      data-testid={testId}
      onSubmit={submit}
      className="bg-white border border-slate-200 p-6 sticky top-6"
    >
      <div className="font-display text-lg text-navy mb-4">{title}</div>
      <div className="space-y-3">
        {fields.map((f) => (
          <div key={f.name}>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">
              {f.label}
            </label>
            {f.type === "textarea" ? (
              <textarea
                rows={f.rows || 3}
                name={f.name}
                value={form[f.name]}
                onChange={change}
                required={f.required}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
            ) : (
              <input
                type={f.type || "text"}
                name={f.name}
                value={form[f.name]}
                onChange={change}
                required={f.required}
                placeholder={f.placeholder}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-navy focus:outline-none"
              />
            )}
          </div>
        ))}
      </div>
      <button
        type="submit"
        disabled={busy}
        className="mt-5 inline-flex items-center gap-2 bg-navy text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-navy-700 disabled:opacity-60"
      >
        <Plus size={14} /> Create
      </button>
    </form>
  );
}

function ListItem({ title, sub, body, onDelete, testId }) {
  return (
    <div data-testid={testId} className="px-6 py-5 border-t border-slate-100 flex gap-4 items-start">
      <div className="flex-1">
        <div className="font-display text-base text-navy">{title}</div>
        {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
        {body ? <div className="mt-2 text-sm text-slate-600">{body}</div> : null}
      </div>
      <button
        onClick={onDelete}
        className="text-slate-400 hover:text-rose-600 transition"
        aria-label="delete"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}

function BlogPanel({ blog, onCreate, onDelete, busy }) {
  return (
    <PanelShell
      testId="admin-blog-panel"
      title="Insights"
      formNode={
        <CreateBox
          testId="blog-create-form"
          title="New Insight"
          busy={busy}
          fields={[
            { name: "title", label: "Title", required: true },
            { name: "slug", label: "Slug", required: true, placeholder: "url-friendly-slug" },
            { name: "category", label: "Category", default: "Insights" },
            { name: "cover_image", label: "Cover Image URL" },
            { name: "excerpt", label: "Excerpt", type: "textarea", required: true },
            { name: "content", label: "Content (HTML allowed)", type: "textarea", rows: 6, required: true },
          ]}
          onSubmit={onCreate}
        />
      }
    >
      {blog.length === 0 ? (
        <div className="p-10 text-slate-500">No insights yet.</div>
      ) : (
        blog.map((p) => (
          <ListItem
            key={p.id}
            testId={`admin-blog-item-${p.slug}`}
            title={p.title}
            sub={`${p.category} · /${p.slug}`}
            body={p.excerpt}
            onDelete={() => onDelete(p.id)}
          />
        ))
      )}
    </PanelShell>
  );
}

function TestimonialPanel({ items, onCreate, onDelete, busy }) {
  return (
    <PanelShell
      testId="admin-testimonials-panel"
      title="Testimonials"
      formNode={
        <CreateBox
          testId="testimonial-create-form"
          title="New Testimonial"
          busy={busy}
          fields={[
            { name: "name", label: "Name", required: true },
            { name: "role", label: "Role", required: true },
            { name: "organization", label: "Organisation", required: true },
            { name: "quote", label: "Quote", type: "textarea", required: true, rows: 4 },
          ]}
          onSubmit={onCreate}
        />
      }
    >
      {items.length === 0 ? (
        <div className="p-10 text-slate-500">No testimonials yet.</div>
      ) : (
        items.map((t) => (
          <ListItem
            key={t.id}
            testId={`admin-testimonial-item-${t.id}`}
            title={t.name}
            sub={`${t.role} · ${t.organization}`}
            body={`“${t.quote}”`}
            onDelete={() => onDelete(t.id)}
          />
        ))
      )}
    </PanelShell>
  );
}

function FAQPanel({ items, onCreate, onDelete, busy }) {
  return (
    <PanelShell
      testId="admin-faqs-panel"
      title="FAQs"
      formNode={
        <CreateBox
          testId="faq-create-form"
          title="New FAQ"
          busy={busy}
          fields={[
            { name: "question", label: "Question", required: true },
            { name: "answer", label: "Answer", type: "textarea", rows: 4, required: true },
            { name: "category", label: "Category", default: "General" },
            { name: "order", label: "Order", type: "number", default: "0", transform: (v) => Number(v || 0) },
          ]}
          onSubmit={onCreate}
        />
      }
    >
      {items.length === 0 ? (
        <div className="p-10 text-slate-500">No FAQs yet.</div>
      ) : (
        items.map((f) => (
          <ListItem
            key={f.id}
            testId={`admin-faq-item-${f.id}`}
            title={f.question}
            sub={f.category}
            body={f.answer}
            onDelete={() => onDelete(f.id)}
          />
        ))
      )}
    </PanelShell>
  );
}

function ProgramPanel({ items, onCreate, onDelete, busy }) {
  return (
    <PanelShell
      testId="admin-programs-panel"
      title="Programs"
      formNode={
        <CreateBox
          testId="program-create-form"
          title="New Program"
          busy={busy}
          fields={[
            { name: "title", label: "Title", required: true },
            { name: "summary", label: "Summary", type: "textarea", rows: 3, required: true },
            { name: "location", label: "Location", required: true },
            { name: "duration", label: "Duration", required: true, default: "7 days" },
            { name: "category", label: "Category", default: "Educational Tourism" },
            { name: "cover_image", label: "Cover Image URL" },
            {
              name: "highlights",
              label: "Highlights (one per line)",
              type: "textarea",
              rows: 3,
              transform: (v) => (v || "").split("\n").map((s) => s.trim()).filter(Boolean),
            },
          ]}
          onSubmit={onCreate}
        />
      }
    >
      {items.length === 0 ? (
        <div className="p-10 text-slate-500">No programs yet.</div>
      ) : (
        items.map((p) => (
          <ListItem
            key={p.id}
            testId={`admin-program-item-${p.id}`}
            title={p.title}
            sub={`${p.location} · ${p.duration} · ${p.category}`}
            body={p.summary}
            onDelete={() => onDelete(p.id)}
          />
        ))
      )}
    </PanelShell>
  );
}

function EventPanel({ items, onCreate, onDelete, busy }) {
  return (
    <PanelShell
      testId="admin-events-panel"
      title="Events"
      formNode={
        <CreateBox
          testId="event-create-form"
          title="New Event"
          busy={busy}
          fields={[
            { name: "title", label: "Title", required: true },
            { name: "summary", label: "Summary", type: "textarea", rows: 3, required: true },
            { name: "event_type", label: "Type", required: true, default: "Conference" },
            { name: "location", label: "Location", required: true },
            { name: "date_label", label: "Date label", required: true, placeholder: "Spring 2026" },
            { name: "cover_image", label: "Cover Image URL" },
          ]}
          onSubmit={onCreate}
        />
      }
    >
      {items.length === 0 ? (
        <div className="p-10 text-slate-500">No events yet.</div>
      ) : (
        items.map((e) => (
          <ListItem
            key={e.id}
            testId={`admin-event-item-${e.id}`}
            title={e.title}
            sub={`${e.event_type} · ${e.location} · ${e.date_label}`}
            body={e.summary}
            onDelete={() => onDelete(e.id)}
          />
        ))
      )}
    </PanelShell>
  );
}
